import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  MoneyHand24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
  Checkmark20Regular,
  ChevronDown20Regular,
  ChevronUp20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getDebtOverview,
  getLoanById,
  getLoans,
  registerLoanPayment,
  type DebtOverview,
  type DebtOverviewLine,
  type Loan as ApiLoan,
} from "../../emprestimos/emprestimosApi";
import { APP_LOCALE } from "../../config/locale";

interface EmprestimoView {
  id: number;
  entidadeFinanceira: string;
  descricao?: string | null;
  prestacao?: number | null;
  dataVencimento?: string | null;
  periodicidade?: string | null;
  estado?: string | null;
  proximoDebito?: string | null;
  saldoEmDivida?: number | null;
}

const SUCCESS_STYLE = { color: "var(--color-success, #16a34a)" } as const;

const formatOptional = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value.trim() ? value : "—";
  return String(value);
};

const formatCurrency = (value?: number | null): string => {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar empréstimos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os empréstimos.";
};

const mapLoanToView = (loan: ApiLoan): EmprestimoView => {
  return {
    id: loan.id,
    entidadeFinanceira: loan.financialEntity,
    descricao: loan.description ?? null,
    // Prefere a prestação calculada (plano de amortização) sobre o valor legacy.
    prestacao: loan.installmentAmount ?? loan.installmentValue ?? null,
    dataVencimento: loan.dueDate ?? null,
    periodicidade: loan.periodicity ?? null,
    estado: loan.status ?? null,
    // Próximo débito: prestação calculada; só nos empréstimos legacy (sem plano) o vencimento
    // manual — com plano e tudo pago, a data antiga do formulário enganava.
    proximoDebito:
      loan.installmentAmount == null
        ? loan.dueDate ?? null
        : loan.nextInstallment?.dueDate ?? null,
    saldoEmDivida: loan.outstandingBalance ?? null,
  };
};

const formatSplit = (
  principal?: number | null,
  interest?: number | null,
): string | null => {
  if (
    principal === undefined ||
    principal === null ||
    interest === undefined ||
    interest === null
  ) {
    return null;
  }
  return `Capital ${formatCurrency(principal)} · Juro ${formatCurrency(interest)}`;
};

const DEBT_KIND_META: Record<
  DebtOverviewLine["kind"],
  { label: string; badgeClass: string }
> = {
  loan: { label: "Empréstimo", badgeClass: "bg-primary/10 text-primary" },
  confirming: {
    label: "Confirming",
    badgeClass: "bg-yellow-500/10 text-yellow-700",
  },
};

const isWideViewport = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(min-width: 768px)").matches;

interface DebtOverviewPanelProps {
  /** No telemóvel, com o detalhe de um empréstimo aberto, o painel sai do caminho. */
  hidden: boolean;
  reloadKey: number;
  onSelectLoan: (loanId: number) => void;
  onOpenConfirming: () => void;
}

/**
 * Posição da dívida (empréstimos + Confirming): o que se paga a 30/60/90 dias, quanto falta pagar
 * e a lista consolidada dos próximos débitos (capital + juro). Os cálculos vêm todos do backend.
 */
function DebtOverviewPanel({
  hidden,
  reloadKey,
  onSelectLoan,
  onOpenConfirming,
}: DebtOverviewPanelProps) {
  const [overview, setOverview] = useState<DebtOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Num telemóvel começa recolhido para não tapar a lista de empréstimos.
  const [isOpen, setIsOpen] = useState<boolean>(isWideViewport);

  useEffect(() => {
    let isMounted = true;

    getDebtOverview()
      .then((response) => {
        if (!isMounted) return;
        setOverview(response);
        setError(null);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(parseErrorMessage(err));
      });

    return () => {
      isMounted = false;
    };
  }, [reloadKey]);

  const handleLineClick = (line: DebtOverviewLine) => {
    // No telemóvel o detalhe abre por baixo do painel: fechá-lo deixa-lhe o ecrã todo.
    if (!isWideViewport()) setIsOpen(false);
    if (line.kind === "loan") {
      onSelectLoan(line.id);
    } else {
      onOpenConfirming();
    }
  };

  return (
    <section className={`px-4 md:px-6 pt-4 ${hidden ? "hidden md:block" : ""}`}>
      <div
        className="bg-card border border-border"
        style={{ borderRadius: "var(--radius-card)" }}
      >
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          className="w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[44px] text-left"
          aria-expanded={isOpen}
        >
          <span className="min-w-0">
            <span
              className="block text-foreground"
              style={{ fontWeight: "var(--font-weight-semi-bold)" }}
            >
              Posição da dívida
            </span>
            <span
              className="block text-secondary-text mt-0.5"
              style={{ fontSize: "var(--text-caption)" }}
            >
              {error
                ? error
                : overview
                  ? overview.includesConfirming
                    ? `Em dívida ${formatCurrency(overview.totalOutstanding)} · Empréstimos ${formatCurrency(overview.loansOutstanding)} · Confirming ${formatCurrency(overview.confirmingOutstanding)}${
                        overview.confirmingPlafond
                          ? ` (plafond usado ${formatCurrency(overview.confirmingPlafondUsed ?? 0)} de ${formatCurrency(overview.confirmingPlafond)}, disponível ${formatCurrency(overview.confirmingPlafondAvailable ?? 0)})`
                          : ""
                      }`
                    : `Em dívida ${formatCurrency(overview.totalOutstanding)}`
                  : "A carregar posição da dívida..."}
            </span>
          </span>
          {isOpen ? (
            <ChevronUp20Regular className="w-5 h-5 text-secondary-text flex-shrink-0" />
          ) : (
            <ChevronDown20Regular className="w-5 h-5 text-secondary-text flex-shrink-0" />
          )}
        </button>

        {isOpen && overview && (
          <div className="border-t border-border p-4 space-y-4">
            {overview.overdueTotal > 0 && (
              <div
                className="bg-red-500/10 text-red-700 px-4 py-3 text-sm"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                Débitos vencidos por pagar:{" "}
                <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                  {formatCurrency(overview.overdueTotal)}
                </span>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-3">
              {overview.horizons.map((horizon) => (
                <div
                  key={horizon.days}
                  className="bg-muted/30 border border-border p-4"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Próximos {horizon.days} dias
                    {/* Os horizontes acumulam o que já venceu e não se pagou: sem o aviso, o
                        "30 dias" parecia só o que aí vem. */}
                    {overview.overdueTotal > 0 && " (inclui vencidos)"}
                  </p>
                  <p
                    className="text-foreground mt-1"
                    style={{
                      fontSize: "var(--text-large)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    {formatCurrency(horizon.total)}
                  </p>
                  <p
                    className="text-secondary-text mt-1"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    {formatSplit(horizon.principal, horizon.interest)}
                  </p>
                  {/* O "quanto falta" de cada data: o saldo que sobra depois destes débitos. */}
                  <p
                    className="text-foreground mt-1"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Fica em dívida a {formatDate(horizon.until)}{" "}
                    <span style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                      {formatCurrency(horizon.outstandingAfter)}
                    </span>
                  </p>
                  {overview.includesConfirming && (
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Empréstimos {formatCurrency(horizon.loansTotal)} · Confirming{" "}
                      {formatCurrency(horizon.confirmingTotal)}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div>
              <p
                className="text-foreground mb-1"
                style={{ fontWeight: "var(--font-weight-semi-bold)" }}
              >
                Próximos débitos
              </p>
              {overview.lines.length === 0 ? (
                <p className="text-secondary-text text-sm">
                  Sem empréstimos nem Confirming em dívida.
                </p>
              ) : (
                <div
                  className="overflow-x-auto border border-border"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <table className="w-full" style={{ minWidth: 720 }}>
                    <thead>
                      <tr className="border-b border-border bg-muted text-left text-secondary-text text-sm">
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2">Entidade</th>
                        <th className="px-3 py-2">Descrição</th>
                        <th className="px-3 py-2 text-right">Em dívida</th>
                        <th className="px-3 py-2">Próximo débito</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.lines.map((line) => {
                        const kind = DEBT_KIND_META[line.kind];
                        const split = formatSplit(
                          line.nextPrincipal,
                          line.nextInterest,
                        );
                        return (
                          <tr
                            key={`${line.kind}-${line.id}`}
                            className="border-b border-border last:border-b-0 cursor-pointer hover:bg-accent/50"
                            tabIndex={0}
                            onClick={() => handleLineClick(line)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleLineClick(line);
                              }
                            }}
                          >
                            <td className="px-3 py-2">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs ${kind.badgeClass}`}
                                style={{
                                  borderRadius: "var(--radius-button)",
                                  fontWeight: "var(--font-weight-semi-bold)",
                                }}
                              >
                                {kind.label}
                              </span>
                            </td>
                            <td className="px-3 py-2">{line.entity}</td>
                            <td className="px-3 py-2 text-secondary-text">
                              {formatOptional(line.description)}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {formatCurrency(line.outstandingBalance)}
                            </td>
                            <td
                              className={`px-3 py-2 whitespace-nowrap ${
                                line.isOverdue ? "text-red-700" : ""
                              }`}
                            >
                              {formatDate(line.nextDueDate)}
                              {line.isOverdue && (
                                <span className="text-xs"> (vencido)</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <span className="block">
                                {formatCurrency(line.nextAmount)}
                              </span>
                              {split && (
                                <span
                                  className="block text-secondary-text"
                                  style={{ fontSize: "var(--text-caption)" }}
                                >
                                  {split}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const getInitials = (nome: string) => {
  return nome
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export function EmprestimosView() {
  const navigate = useNavigate();
  const [emprestimos, setEmprestimos] = useState<EmprestimoView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [selectedLoanDetail, setSelectedLoanDetail] = useState<ApiLoan | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  // Incrementado após registar uma prestação para refrescar a posição da dívida.
  const [overviewReloadKey, setOverviewReloadKey] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const loadLoans = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getLoans();
        if (!isMounted) return;

        const mapped = response.map(mapLoanToView);
        setEmprestimos(mapped);
        setSelectedLoanId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setEmprestimos([]);
        setSelectedLoanId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLoans();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredEmprestimos = useMemo(
    () =>
      emprestimos.filter((emprestimo) => {
        const matchesSearch =
          emprestimo.entidadeFinanceira
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (emprestimo.descricao ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
      }),
    [emprestimos, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredEmprestimos.length === 0) {
      setSelectedLoanId(null);
      return;
    }

    const hasSelection = filteredEmprestimos.some(
      (entry) => entry.id === selectedLoanId,
    );
    if (!hasSelection) {
      setSelectedLoanId(filteredEmprestimos[0].id);
    }
  }, [filteredEmprestimos, isLoading, selectedLoanId]);

  useEffect(() => {
    if (!selectedLoanId) {
      setSelectedLoanDetail(null);
      return;
    }

    let isMounted = true;
    // Reinicia o estado do detalhe ao mudar de empréstimo.
    setSelectedLoanDetail(null);
    setIsScheduleOpen(false);
    setRegisterError(null);

    const loadSelectedLoan = async () => {
      try {
        const response = await getLoanById(selectedLoanId);
        if (!isMounted) return;

        setSelectedLoanDetail(response);
        const mapped = mapLoanToView(response);
        setEmprestimos((current) =>
          current.map((emprestimo) =>
            emprestimo.id === selectedLoanId ? mapped : emprestimo,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedLoan();

    return () => {
      isMounted = false;
    };
  }, [selectedLoanId]);

  const selectedEmprestimo = filteredEmprestimos.find(
    (e) => e.id === selectedLoanId,
  );

  const detail =
    selectedLoanDetail && selectedLoanDetail.id === selectedLoanId
      ? selectedLoanDetail
      : null;
  const schedule = detail?.schedule ?? [];
  const hasPlan = schedule.length > 0;
  const paidCount = detail?.paidInstallmentsCount ?? 0;
  const totalCount = detail?.numberOfInstallments ?? schedule.length;
  const allPaid = hasPlan && paidCount >= totalCount;

  const handleRegisterPayment = async () => {
    if (!detail || isRegistering) return;
    setIsRegistering(true);
    setRegisterError(null);
    try {
      const updated = await registerLoanPayment(detail.id);
      setSelectedLoanDetail(updated);
      const mapped = mapLoanToView(updated);
      setEmprestimos((current) =>
        current.map((emprestimo) =>
          emprestimo.id === updated.id ? mapped : emprestimo,
        ),
      );
      setOverviewReloadKey((key) => key + 1);
    } catch (error) {
      setRegisterError(
        error instanceof Error
          ? error.message
          : "Não foi possível registar a prestação.",
      );
    } finally {
      setIsRegistering(false);
    }
  };

  const handleLoanSelect = (loanId: number) => {
    setSelectedLoanId(loanId);
    setIsMobileDetailOpen(true);
  };

  const handleOverviewLoanSelect = (loanId: number) => {
    // Limpa a pesquisa para o empréstimo não ficar escondido pelo filtro.
    setSearchQuery("");
    handleLoanSelect(loanId);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/emprestimos/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Empréstimo
      </button>
    </>
  );

  return (
    // A página faz scroll: com a posição da dívida aberta, a lista e o detalhe ficavam espremidos numa
    // faixa de ~250 px, com scroll só lá dentro.
    <div className="flex flex-col h-full overflow-y-auto">
      <PageHeader
        title="Empréstimos"
        subtitle="Lista de empréstimos e responsabilidades financeiras"
        actions={actions}
      />

      <DebtOverviewPanel
        hidden={isMobileDetailOpen}
        reloadKey={overviewReloadKey}
        onSelectLoan={handleOverviewLoanSelect}
        onOpenConfirming={() => navigate("/confirming")}
      />

      <div className="flex-1 flex overflow-hidden flex-shrink-0" style={{ minHeight: "75vh" }}>
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar empréstimos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar empréstimos..."
                : `${filteredEmprestimos.length} empréstimo${
                    filteredEmprestimos.length !== 1 ? "s" : ""
                  } encontrado${
                    filteredEmprestimos.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <MoneyHand24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar empréstimos...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <MoneyHand24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredEmprestimos.map((emprestimo) => (
                <button
                  key={emprestimo.id}
                  onClick={() => handleLoanSelect(emprestimo.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedLoanId === emprestimo.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedLoanId === emprestimo.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedLoanId !== emprestimo.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedLoanId !== emprestimo.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedLoanId === emprestimo.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div
                      className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(emprestimo.entidadeFinanceira)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {emprestimo.entidadeFinanceira}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(emprestimo.descricao)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatCurrency(emprestimo.prestacao)}
                      </p>

                      {(emprestimo.proximoDebito ||
                        (emprestimo.saldoEmDivida !== null &&
                          emprestimo.saldoEmDivida !== undefined)) && (
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          {[
                            emprestimo.proximoDebito
                              ? `Próx. débito ${formatDate(emprestimo.proximoDebito)}`
                              : null,
                            emprestimo.saldoEmDivida !== null &&
                            emprestimo.saldoEmDivida !== undefined
                              ? `Em dívida ${formatCurrency(emprestimo.saldoEmDivida)}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredEmprestimos.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum empréstimo encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedEmprestimo ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <MoneyHand24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um empréstimo para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-large)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(selectedEmprestimo.entidadeFinanceira)}
                    </div>

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedEmprestimo.entidadeFinanceira}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/emprestimos/editar/${selectedEmprestimo.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Empréstimo</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Amortização — prestação devida + saldo em dívida + registar pagamento */}
              {hasPlan && (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div
                      className="bg-card border border-border p-5"
                      style={{ borderRadius: "var(--radius-card)" }}
                    >
                      <p
                        className="text-secondary-text"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        Prestação devida
                      </p>
                      {allPaid ? (
                        <p
                          className="mt-1"
                          style={{
                            fontSize: "var(--text-large)",
                            fontWeight: "var(--font-weight-semi-bold)",
                            ...SUCCESS_STYLE,
                          }}
                        >
                          Todas as prestações pagas
                        </p>
                      ) : (
                        <>
                          <p
                            className="text-foreground mt-1"
                            style={{
                              fontSize: "var(--text-large)",
                              fontWeight: "var(--font-weight-semi-bold)",
                            }}
                          >
                            {formatCurrency(
                              detail?.nextInstallment?.amount ??
                                detail?.installmentAmount,
                            )}
                          </p>
                          {detail?.nextInstallment && (
                            <>
                              <p
                                className="text-secondary-text mt-1"
                                style={{ fontSize: "var(--text-caption)" }}
                              >
                                {formatSplit(
                                  detail.nextInstallment.principalPortion,
                                  detail.nextInstallment.interestPortion,
                                )}
                              </p>
                              <p
                                className="text-secondary-text"
                                style={{ fontSize: "var(--text-caption)" }}
                              >
                                Próxima (nº {detail.nextInstallment.number}):{" "}
                                {formatDate(detail.nextInstallment.dueDate)}
                              </p>
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <div
                      className="bg-card border border-border p-5"
                      style={{ borderRadius: "var(--radius-card)" }}
                    >
                      <p
                        className="text-secondary-text"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        Saldo em dívida
                      </p>
                      <p
                        className="text-foreground mt-1"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                        }}
                      >
                        {formatCurrency(detail?.outstandingBalance)}
                      </p>
                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {paidCount}/{totalCount} pagas
                      </p>
                    </div>
                  </div>

                  {registerError && (
                    <div
                      className="bg-card border border-[var(--destructive)] px-4 py-3 text-[var(--destructive)]"
                      style={{ borderRadius: "var(--radius-card)" }}
                    >
                      {registerError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px] disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={handleRegisterPayment}
                      disabled={isRegistering || allPaid}
                    >
                      <Checkmark20Regular className="w-5 h-5" />
                      {isRegistering
                        ? "A registar..."
                        : "Registar prestação paga"}
                    </button>
                    {allPaid && (
                      <span
                        className="text-sm"
                        style={SUCCESS_STYLE}
                      >
                        Empréstimo liquidado.
                      </span>
                    )}
                  </div>

                  {/* Mapa de prestações (colapsável) */}
                  <div
                    className="bg-card border border-border"
                    style={{ borderRadius: "var(--radius-card)" }}
                  >
                    <button
                      type="button"
                      onClick={() => setIsScheduleOpen((open) => !open)}
                      className="w-full flex items-center justify-between gap-3 px-5 py-4 min-h-[44px]"
                      aria-expanded={isScheduleOpen}
                    >
                      <span
                        className="text-foreground"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        Mapa de prestações ({schedule.length})
                      </span>
                      {isScheduleOpen ? (
                        <ChevronUp20Regular className="w-5 h-5 text-secondary-text" />
                      ) : (
                        <ChevronDown20Regular className="w-5 h-5 text-secondary-text" />
                      )}
                    </button>

                    {isScheduleOpen && (
                      <div className="overflow-x-auto border-t border-border">
                        <table className="w-full" style={{ minWidth: 640 }}>
                          <thead>
                            <tr className="border-b border-border bg-muted text-left text-secondary-text text-sm">
                              <th className="px-3 py-2">Nº</th>
                              <th className="px-3 py-2">Data</th>
                              <th className="px-3 py-2 text-right">Prestação</th>
                              <th className="px-3 py-2 text-right">Capital</th>
                              <th className="px-3 py-2 text-right">Juro</th>
                              <th className="px-3 py-2 text-right">
                                Saldo após
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {schedule.map((installment) => (
                              <tr
                                key={installment.number}
                                className={`border-b border-border last:border-b-0 ${
                                  installment.paid ? "bg-green-500/10" : ""
                                }`}
                              >
                                <td className="px-3 py-2">
                                  <span className="inline-flex items-center gap-1.5">
                                    {installment.paid && (
                                      <Checkmark20Regular
                                        className="w-4 h-4"
                                        style={SUCCESS_STYLE}
                                        aria-label="Paga"
                                      />
                                    )}
                                    {installment.number}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {formatDate(installment.dueDate)}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {formatCurrency(installment.amount)}
                                  {installment.paidOn && (
                                    <div className="text-xs text-secondary-text">
                                      pago a {formatDate(installment.paidOn)}
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap text-secondary-text">
                                  {formatCurrency(installment.principalPortion)}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap text-secondary-text">
                                  {formatCurrency(installment.interestPortion)}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                  {formatCurrency(installment.outstandingAfter)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {/* Totais do mapa: o que já saiu (valores reais) e o que falta, em capital e juro. */}
                          <tfoot>
                            {[
                              { label: "Pago", rows: schedule.filter((line) => line.paid) },
                              { label: "Por pagar", rows: schedule.filter((line) => !line.paid) },
                            ].map(({ label, rows }) => (
                              <tr key={label} className="border-t border-border bg-muted/30">
                                <td className="px-3 py-2" colSpan={2} style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                                  {`${label} (${rows.length})`}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>
                                  {formatCurrency(rows.reduce((sum, line) => sum + line.amount, 0))}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap text-secondary-text">
                                  {formatCurrency(rows.reduce((sum, line) => sum + line.principalPortion, 0))}
                                </td>
                                <td className="px-3 py-2 text-right whitespace-nowrap text-secondary-text">
                                  {formatCurrency(rows.reduce((sum, line) => sum + line.interestPortion, 0))}
                                </td>
                                <td className="px-3 py-2" />
                              </tr>
                            ))}
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Empréstimo
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Descrição
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedEmprestimo.descricao)}
                    </p>
                  </div>

                  {hasPlan && (
                    <>
                      <div>
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          Capital inicial
                        </p>
                        <p className="mt-0.5">
                          {formatCurrency(detail?.principal)}
                        </p>
                      </div>

                      <div>
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          Taxa anual (TAN)
                        </p>
                        <p className="mt-0.5">
                          {detail?.annualInterestRatePercent !== null &&
                          detail?.annualInterestRatePercent !== undefined
                            ? `${detail.annualInterestRatePercent}%`
                            : "—"}
                        </p>
                      </div>
                    </>
                  )}

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Valor / Prestação
                    </p>
                    <p className="mt-0.5">
                      {formatCurrency(selectedEmprestimo.prestacao)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      {hasPlan ? "Data da 1ª prestação" : "Data de Vencimento"}
                    </p>
                    <p className="mt-0.5">
                      {formatDate(
                        hasPlan
                          ? detail?.firstInstallmentDate
                          : selectedEmprestimo.dataVencimento,
                      )}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Periodicidade
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedEmprestimo.periodicidade)}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Estado
                    </p>
                    <p className="mt-0.5">
                      {formatOptional(selectedEmprestimo.estado)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
